import React, { useEffect, useMemo, useState } from "react";
import api, { buildWaLink, formatIDR } from "../lib/api";
import { sendWaSmart } from "../lib/wa";
import Layout from "../components/Layout";
import { ClipboardList, Users, Wrench, Wallet, Calendar, Bell, MessageCircle, Send, Copy, MapPin, Eye, EyeOff } from "lucide-react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import TechPresence, { APP_STATUS } from "../components/TechPresence";

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-white border border-[#E4E4E7] p-4 sm:p-6" data-testid={testid}>
    <div className="flex items-center gap-2 mb-2 sm:mb-3">
      <Icon size={14} className="text-[#52525B]" />
      <div className="label-cap text-[10px] sm:text-xs">{label}</div>
    </div>
    <div className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tighter break-words leading-tight">{value}</div>
  </div>
);

const STATUS_COLORS = {
  Dijadwalkan: "bg-[#2563EB]/10 text-[#2563EB] border-[#2563EB]/20",
  Berjalan: "bg-[#D97706]/10 text-[#D97706] border-[#D97706]/20",
  Selesai: "bg-[#059669]/10 text-[#059669] border-[#059669]/20",
  Dibatalkan: "bg-[#DC2626]/10 text-[#DC2626] border-[#DC2626]/20",
};

// tanggal lokal YYYY-MM-DD (toISOString() memakai UTC → di WIB tgl 1 jam 00:00 jadi tgl terakhir bulan lalu)
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const firstOfMonth = () => { const d = new Date(); return ymd(new Date(d.getFullYear(), d.getMonth(), 1)); };
const lastOfMonth = () => { const d = new Date(); return ymd(new Date(d.getFullYear(), d.getMonth() + 1, 0)); };

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState({ month: "", cities: [], payments: [] });
  const [reminders, setReminders] = useState({ target_date: "", orders: [] });
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(lastOfMonth());
  const [daysAhead, setDaysAhead] = useState(1);
  const [attendance, setAttendance] = useState({ date: "", items: [], bases: [] });
  const [placeTech, setPlaceTech] = useState(null);
  const [placeForm, setPlaceForm] = useState({ outside_base: false, outside_base_start: "", outside_base_end: "", outside_base_to: "", note: "" });
  const [placeSaving, setPlaceSaving] = useState(false);
  const [waGateway, setWaGateway] = useState(false);
  const [unpaid, setUnpaid] = useState({ orders: [], count: 0, total_sisa: 0 });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkCust, setBulkCust] = useState(true);
  const [bulkTech, setBulkTech] = useState(true);
  const [bulkSending, setBulkSending] = useState(false);
  const [cityModal, setCityModal] = useState(null);
  const [cityOrders, setCityOrders] = useState([]);
  const [cityLoading, setCityLoading] = useState(false);
  const [showMoney, setShowMoney] = useState(false);
  const money = (v) => (showMoney ? formatIDR(v) : "Rp ••••••");
  const toggleMoney = () => setShowMoney((s) => !s);

  const openCityOrders = async (city) => {
    setCityModal(city);
    setCityLoading(true);
    try {
      const m = insights.month || new Date().toISOString().slice(0, 7);
      const { data } = await api.get("/orders", { params: { schedule_from: `${m}-01`, schedule_to: `${m}-31`, sort: "schedule_asc" } });
      const match = (o) => {
        const c = ((o.customer?.city || "").trim().toLowerCase());
        return city === "Tanpa Kota" ? !c : c === city.toLowerCase();
      };
      setCityOrders((data || []).filter((o) => o.work_status !== "Dibatalkan" && match(o)));
    } catch (_) {
      setCityOrders([]);
    } finally {
      setCityLoading(false);
    }
  };

  const loadAttendance = () => {
    api.get("/attendance/today").then((r) => setAttendance({ bases: [], ...r.data })).catch(() => {});
  };
  const baseLabel = (k) => (attendance.bases || []).find((b) => b.key === k)?.label || "";
  const openPlacement = (a) => {
    if (!a.nik) return;
    const today = attendance.date || new Date(Date.now() + 7 * 3600e3).toISOString().slice(0, 10);
    setPlaceTech(a);
    setPlaceForm({
      outside_base: !!a.outside_base,
      outside_base_start: a.outside_base_start || (a.outside_base ? "" : today),
      outside_base_end: a.outside_base_end || "",
      outside_base_to: a.outside_base_to || "",
      note: "",
    });
  };
  const savePlacement = async () => {
    if (!placeTech) return;
    if (placeForm.outside_base && !placeForm.outside_base_end) { toast.error("Tanggal akhir penempatan wajib diisi"); return; }
    setPlaceSaving(true);
    try {
      await api.put(`/technicians/${placeTech.technician_id}/placement`, placeForm.outside_base ? placeForm : { outside_base: false });
      toast.success(`Penempatan ${placeTech.name} disimpan`);
      setPlaceTech(null);
      loadAttendance();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan penempatan");
    } finally {
      setPlaceSaving(false);
    }
  };

  const loadDashboard = () => {
    api.get("/reports/dashboard", { params: { start, end } }).then((r) => setData(r.data)).catch(() => {});
  };
  const loadInsights = () => {
    api.get("/dashboard/monthly-insights").then((r) => setInsights(r.data || { cities: [], payments: [] })).catch(() => {});
  };
  const loadReminders = (d = daysAhead) => {
    api.get("/reports/reminders", { params: { days: d } }).then((r) => setReminders(r.data)).catch(() => {});
  };

  useEffect(() => { loadDashboard(); /* eslint-disable-next-line */ }, [start, end]);
  useEffect(() => { loadInsights(); loadReminders(1); loadAttendance(); api.get("/settings").then((r) => setWaGateway(!!r.data.wa_gateway_enabled)).catch(() => {}); api.get("/reports/unpaid").then((r) => setUnpaid(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, []);

  const chartMax = useMemo(() => Math.max(1, ...insights.cities.map((c) => c.customers || 0)), [insights.cities]);

  const setPreset = (preset) => {
    const now = new Date();
    if (preset === "month") { setStart(firstOfMonth()); setEnd(lastOfMonth()); }
    else if (preset === "last30") {
      const e = ymd(now);
      const s = ymd(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29));
      setStart(s); setEnd(e);
    } else if (preset === "ytd") {
      setStart(`${now.getFullYear()}-01-01`);
      setEnd(ymd(now));
    } else if (preset === "all") { setStart(""); setEnd(""); }
  };

  const reminderDayLabel = () => (daysAhead === 0 ? "HARI INI" : daysAhead === 1 ? "BESOK" : `TANGGAL ${reminders.target_date || "-"}`);

  const sendReminderCustomer = (o) => {
    const itemsText = (o.items || []).map((i) => `• ${i.description} (${i.qty}x)`).join("\n");
    const itemsFlat = (o.items || []).map((i) => `${i.description} (${i.qty}x)`).join(", ") || "-";
    const text = `Halo KK ${o.customer?.name || ""}, mengingatkan jadwal pekerjaan di tempat Anda ${reminderDayLabel()} :\n\nOrder: *${o.order_no}*\nTanggal: *${o.schedule_date}*\n\nLayanan:\n${itemsText}\n\nMohon konfirmasi ketersediaan di lokasi. Terima kasih.`;
    if (!o.customer?.phone) { toast.error("Nomor pelanggan kosong"); return; }
    sendWaSmart({
      enabled: waGateway, phone: o.customer.phone, text, name: o.customer?.name || "",
      template: "pengingat",
      params: [o.customer?.name || "-", reminderDayLabel(), o.order_no || "-", o.schedule_date || "-", itemsFlat],
    });
  };

  const sendReminderTechnician = async (o) => {
    const fromAssignments = (o.assignments || []).map((a) => a.technician).filter((t) => t && (t.phone || "").trim());
    const techList = fromAssignments.length ? fromAssignments : (o.technician?.phone ? [o.technician] : []);
    if (!techList.length) { toast.error("Teknisi belum ditugaskan / nomor kosong"); return; }
    const itemsText = (o.items || []).map((i) => `• ${i.description} (${i.qty}x)`).join("\n");
    const itemsFlat = (o.items || []).map((i) => `${i.description} (${i.qty}x)`).join(", ") || "-";
    const loc = (o.customer?.location_link || o.customer?.maps_link || "").trim();
    const mapsHref = loc || `https://maps.google.com/?q=${encodeURIComponent(o.customer?.address || o.customer?.name || "")}`;
    const seen = new Set();
    for (const t of techList) {
      if (seen.has(t.phone)) continue;
      seen.add(t.phone);
      const text = `Halo ${t.name}, pengingat penugasan ${reminderDayLabel()}:\n\nOrder: *${o.order_no}*\nPelanggan: ${o.customer?.name}\nTelepon: ${o.customer?.phone}\nAlamat: ${o.customer?.address || "-"}\n📍 *Google Maps:* ${mapsHref}\nJadwal: *${o.schedule_date}*\n\nPekerjaan:\n${itemsText}\n\nMohon datang tepat waktu. Catatan: ${o.notes || "-"}`;
      await sendWaSmart({
        enabled: waGateway, phone: t.phone, text, name: t.name || "",
        template: "jadwal_teknisi",
        params: [t.name || "-", o.order_no || "-", o.customer?.name || "-", o.customer?.phone || "-", o.customer?.address || "-", mapsHref, o.schedule_date || "-", itemsFlat, o.notes || "-"],
      });
    }
  };

  const sendBulk = async () => {
    if (!bulkCust && !bulkTech) { toast.error("Pilih minimal satu tujuan"); return; }
    setBulkSending(true);
    try {
      const { data } = await api.post("/wa/send-bulk", { days_ahead: daysAhead, send_customers: bulkCust, send_technicians: bulkTech });
      setBulkOpen(false);
      if (data.sent > 0) toast.success(`✅ ${data.sent} pesan pengingat terkirim via gateway (${data.total_orders} order)`);
      if ((data.failed || []).length > 0) {
        toast.error(`⚠️ ${data.failed.length} gagal: ${data.failed.slice(0, 3).map((f) => `${f.order_no} (${f.tujuan})`).join(", ")}${data.failed.length > 3 ? "…" : ""}`, { duration: 10000 });
      }
      if (data.sent === 0 && (data.failed || []).length === 0) toast.error("Tidak ada nomor yang bisa dikirim");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengirim bulk");
    } finally {
      setBulkSending(false);
    }
  };


  const copyScheduleToClipboard = async () => {
    const orders = reminders.orders || [];
    if (orders.length === 0) { toast.error("Tidak ada jadwal untuk disalin"); return; }
    const dateLabel = reminderDayLabel();
    const header = `📋 *JADWAL TEKNISI ${dateLabel}*\n📅 ${reminders.target_date || "-"}\n━━━━━━━━━━━━━━━━━━━`;
    const blocks = orders.map((o, idx) => {
      const items = (o.items || []).map((i) => `  • ${i.description} (${i.qty}x)`).join("\n") || "  -";
      const techs = (o.assignments && o.assignments.length > 0)
        ? o.assignments.map((a) => a.technician?.name || "?").join(", ")
        : (o.technician?.name || "-");
      return `\n${idx + 1}. *${o.order_no}*\n👤 Client: ${o.customer?.name || "-"}\n🏙️ Kota: ${o.customer?.city || "-"}\n📞 Telp: ${o.customer?.phone || "-"}\n🔧 Pekerjaan:\n${items}\n👷 Teknisi: ${techs}`;
    }).join("\n");
    const footer = `\n━━━━━━━━━━━━━━━━━━━\nTotal: ${orders.length} order`;
    const text = header + blocks + footer;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback (non-HTTPS or older browser)
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success(`Jadwal ${dateLabel.toLowerCase()} tersalin ke clipboard (${orders.length} order)`);
    } catch {
      toast.error("Gagal menyalin. Coba pakai browser terbaru.");
    }
  };

  if (!data) {
    return <Layout><div className="label-cap">Memuat data…</div></Layout>;
  }

  return (
    <Layout>
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="label-cap mb-2">Ringkasan Operasi</div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-black tracking-tight">Dashboard</h1>
        </div>
        <div className="flex items-end gap-2 flex-wrap w-full sm:w-auto">
          <div className="flex-1 sm:flex-initial min-w-0">
            <label className="label-cap block mb-1 text-[10px]">Dari</label>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="dashboard-start-input" className="w-full px-2 py-2 border border-[#E4E4E7] text-xs font-mono outline-none focus:border-[#002FA7] bg-white" />
          </div>
          <div className="flex-1 sm:flex-initial min-w-0">
            <label className="label-cap block mb-1 text-[10px]">Sampai</label>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="dashboard-end-input" className="w-full px-2 py-2 border border-[#E4E4E7] text-xs font-mono outline-none focus:border-[#002FA7] bg-white" />
          </div>
          <div className="flex gap-1 w-full sm:w-auto">
            <button onClick={() => setPreset("month")} data-testid="preset-month" className="flex-1 sm:flex-initial px-2 sm:px-3 py-2 text-[10px] font-bold uppercase tracking-wider border border-[#E4E4E7] bg-white hover:bg-[#F4F4F5] whitespace-nowrap">Bln Ini</button>
            <button onClick={() => setPreset("last30")} data-testid="preset-30d" className="flex-1 sm:flex-initial px-2 sm:px-3 py-2 text-[10px] font-bold uppercase tracking-wider border border-[#E4E4E7] bg-white hover:bg-[#F4F4F5] whitespace-nowrap">30 Hari</button>
            <button onClick={() => setPreset("ytd")} data-testid="preset-ytd" className="flex-1 sm:flex-initial px-2 sm:px-3 py-2 text-[10px] font-bold uppercase tracking-wider border border-[#E4E4E7] bg-white hover:bg-[#F4F4F5] whitespace-nowrap">YTD</button>
            <button onClick={() => setPreset("all")} data-testid="preset-all" className="flex-1 sm:flex-initial px-2 sm:px-3 py-2 text-[10px] font-bold uppercase tracking-wider border border-[#E4E4E7] bg-white hover:bg-[#F4F4F5] whitespace-nowrap">Semua</button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E4E4E7] border border-[#E4E4E7] mb-8">
        <StatCard testid="stat-total-orders" icon={ClipboardList} label="Order (rentang)" value={data.totals.orders} />
        <StatCard testid="stat-total-customers" icon={Users} label="Pelanggan" value={data.totals.customers} />
        <StatCard testid="stat-total-technicians" icon={Wrench} label="Karyawan" value={data.totals.technicians} />
        <StatCard testid="stat-revenue" icon={Wallet} label="Pendapatan (Lunas)" value={
          <button type="button" onClick={toggleMoney} title={showMoney ? "Klik untuk sembunyikan" : "Klik untuk lihat"} data-testid="stat-revenue-toggle" className="font-mono text-base sm:text-xl lg:text-3xl inline-flex items-center gap-2 select-none cursor-pointer text-left">
            {money(data.totals.revenue)}
            {showMoney ? <EyeOff size={16} className="text-[#52525B] shrink-0" /> : <Eye size={16} className="text-[#52525B] shrink-0" />}
          </button>
        } />
      </div>

      {/* Reminder H-1 + Weekly Chart */}
      {/* Absensi teknisi hari ini (geo-absen) — dikelompokkan per base */}
      <div className="bg-white border border-[#E4E4E7] mb-6" data-testid="attendance-today-panel">
        <div className="px-4 sm:px-6 py-3 border-b border-[#E4E4E7] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
          <div className="flex items-center gap-2">
            <MapPin size={14} className="text-[#52525B]" />
            <div className="label-cap text-[10px] sm:text-xs">Absensi Teknisi Hari Ini · {attendance.date || "-"}</div>
          </div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#52525B]">
            <span className="text-[#059669]">{attendance.items.filter((a) => a.status === "masuk").length} masuk</span>
            {" · "}
            <span className="text-[#D97706]">{attendance.items.filter((a) => a.status === "telat").length} telat</span>
            {" · "}
            <span className="text-[#7C3AED]">{attendance.items.filter((a) => a.status === "luar_kota").length} luar kota</span>
            {" · "}
            <span>{attendance.items.filter((a) => a.status === "belum_absen").length} belum</span>
          </div>
        </div>
        {attendance.items.length === 0 ? (
          <div className="p-4 text-sm text-[#52525B]" data-testid="attendance-empty">Tidak ada data absensi teknisi.</div>
        ) : (() => {
          const cols = [...(attendance.bases || []).map((b) => ({ key: b.key, label: b.label }))];
          if (attendance.items.some((a) => !a.current_base)) cols.push({ key: "", label: "Lainnya" });
          return (
            <div className={`grid grid-cols-1 sm:grid-cols-2 ${cols.length > 4 ? "xl:grid-cols-5" : "xl:grid-cols-4"} gap-3 p-3 sm:p-4`}>
              {cols.map((c) => {
                const list = attendance.items.filter((a) => (a.current_base || "") === c.key);
                const hadir = list.filter((a) => ["masuk", "telat", "luar_kota"].includes(a.status)).length;
                return (
                  <div key={c.key || "lainnya"} className="border border-[#E4E4E7] flex flex-col min-w-0" data-testid={`attendance-base-${c.key || "lainnya"}`}>
                    <div className="px-3 py-2 border-b border-[#E4E4E7] bg-[#FAFAFA] flex items-center justify-between">
                      <span className="text-xs font-black uppercase tracking-wider">{c.label}</span>
                      <span className="text-[10px] font-mono font-bold text-[#52525B]">{hadir}/{list.length} hadir</span>
                    </div>
                    <div className="p-2 flex flex-col gap-1.5">
                      {list.length === 0 && <div className="text-[11px] text-[#A1A1AA] px-1 py-1">Tidak ada teknisi</div>}
                      {list.map((a) => {
                        const visiting = a.outside_base_active && a.outside_base_to && a.home_base !== a.current_base;
                        return (
                          <div
                            key={a.technician_id}
                            data-testid={`attendance-chip-${a.technician_id}`}
                            onClick={() => openPlacement(a)}
                            title={a.nik ? `${(APP_STATUS[a.app_status] || APP_STATUS.never).label} — klik untuk cek app, lokasi GPS & penempatan base` : "Belum punya NIK"}
                            className={`flex items-center gap-2 border px-2.5 py-1.5 ${a.nik ? "cursor-pointer hover:border-[#7C3AED]" : ""} ${
                              a.status === "masuk" ? "border-[#059669]/30 bg-[#059669]/5" :
                              a.status === "telat" ? "border-[#D97706]/30 bg-[#D97706]/5" :
                              a.status === "libur" ? "border-[#2563EB]/30 bg-[#2563EB]/5" :
                              a.status === "luar_kota" ? "border-[#7C3AED]/30 bg-[#7C3AED]/5" :
                              "border-[#E4E4E7] bg-[#FAFAFA]"}`}>
                            {a.nik && <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${(APP_STATUS[a.app_status] || APP_STATUS.never).dot}`} data-testid={`attendance-app-dot-${a.technician_id}`} />}
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-bold truncate">{a.name}</div>
                              {visiting && <div className="text-[10px] font-bold text-[#7C3AED] truncate">↳ dari {baseLabel(a.home_base) || a.base_location || "-"} · s/d {a.outside_base_end}</div>}
                              {a.outside_base_active && !a.outside_base_to && <div className="text-[10px] font-bold text-[#7C3AED] truncate">Luar base s/d {a.outside_base_end}</div>}
                              {!a.outside_base_active && a.outside_base && a.outside_base_start > (attendance.date || "") && (
                                <div className="text-[10px] text-[#7C3AED] truncate">Mulai {a.outside_base_start}{a.outside_base_to ? ` → ${baseLabel(a.outside_base_to)}` : ""}</div>
                              )}
                            </div>
                            {a.jam_masuk && <span className="text-[11px] font-mono text-[#52525B]">{a.jam_masuk}</span>}
                            <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 whitespace-nowrap ${
                              a.status === "masuk" ? "bg-[#059669] text-white" :
                              a.status === "telat" ? "bg-[#D97706] text-white" :
                              a.status === "libur" ? "bg-[#2563EB] text-white" :
                              a.status === "luar_kota" ? "bg-[#7C3AED] text-white" :
                              a.status === "tanpa_nik" ? "bg-[#E4E4E7] text-[#52525B]" :
                              "bg-[#52525B]/20 text-[#52525B]"}`}>
                              {a.status === "masuk" ? "Masuk" : a.status === "telat" ? "Telat" : a.status === "luar_kota" ? "Luar Kota/BC" : a.status === "libur" ? "Libur" : a.status === "tanpa_nik" ? "Tanpa NIK" : "Belum Absen"}
                            </span>
                            {a.luar_kota_approval === "pending" && (
                              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 border border-[#7C3AED]/40 text-[#7C3AED]" title="Absen Luar Kota / BC menunggu approval">LK?</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 bg-white border border-[#E4E4E7]" data-testid="reminders-panel">
          <div className="px-4 sm:px-6 py-4 border-b border-[#E4E4E7] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2">
              <Bell size={14} className="text-[#D97706]" />
              <div className="label-cap text-[10px] sm:text-xs">Pengingat · {reminders.target_date || "-"}</div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {[0, 1, 2, 7].map((d) => (
                <button
                  key={d}
                  onClick={() => { setDaysAhead(d); loadReminders(d); }}
                  data-testid={`reminder-days-${d}`}
                  className={`px-2 sm:px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border whitespace-nowrap ${daysAhead === d ? "bg-[#111113] text-white border-[#111113]" : "bg-white border-[#E4E4E7]"}`}
                >
                  {d === 0 ? "Hari Ini" : d === 1 ? "Besok" : d === 7 ? "1 Minggu" : `H-${d}`}
                </button>
              ))}
              {(daysAhead === 0 || daysAhead === 1) && (reminders.orders || []).length > 0 && (
                <button
                  onClick={copyScheduleToClipboard}
                  data-testid="copy-schedule-clipboard"
                  className="px-2 sm:px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border border-[#059669] bg-[#059669] text-white hover:bg-[#047857] flex items-center gap-1 whitespace-nowrap"
                  title="Salin jadwal untuk dipaste ke grup WA"
                >
                  <Copy size={12} /> Copy Jadwal
                </button>
              )}
              {waGateway && (daysAhead === 0 || daysAhead === 1) && (reminders.orders || []).length > 0 && (
                <button
                  onClick={() => setBulkOpen(true)}
                  data-testid="bulk-send-open"
                  className="px-2 sm:px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border border-[#002FA7] bg-[#002FA7] text-white hover:bg-[#00238a] flex items-center gap-1 whitespace-nowrap"
                  title="Kirim pengingat WA ke semua via gateway"
                >
                  <Send size={12} /> Kirim Semua
                </button>
              )}
            </div>
          </div>
          <div>
            {(!reminders.orders || reminders.orders.length === 0) && (
              <div className="p-6 text-sm text-[#52525B]">Tidak ada pekerjaan terjadwal untuk tanggal ini.</div>
            )}
            {(reminders.orders || []).map((o) => (
              <div key={o.id} className="px-6 py-4 border-b border-[#E4E4E7] last:border-b-0 flex items-center gap-4 flex-wrap" data-testid={`reminder-row-${o.id}`}>
                <Link to={`/orders?open=${o.id}`} data-testid={`reminder-orderno-${o.order_no}`} className="font-mono text-xs text-[#002FA7] hover:underline font-bold w-28 shrink-0">{o.order_no}</Link>
                <div className="flex-1 min-w-[160px]">
                  <div className="font-bold text-sm truncate">{o.customer?.name || "-"}</div>
                  <div className="text-[11px] font-bold text-[#002FA7] flex items-center gap-1" data-testid={`reminder-city-${o.id}`}><MapPin size={10} className="shrink-0" /> {o.customer?.city || "—"}</div>
                  <div className="text-xs text-[#52525B] truncate">{(o.items || []).map((i) => i.description).join(", ")}</div>
                </div>
                <div className="text-xs">
                  <div className="label-cap mb-0.5">Teknisi</div>
                  <div className="text-sm">{o.technician?.name || "—"}</div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => sendReminderCustomer(o)}
                    data-testid={`reminder-wa-customer-${o.id}`}
                    title="Kirim pengingat ke pelanggan"
                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2.5 py-1.5 border border-[#059669] text-[#059669] hover:bg-[#059669]/10"
                  >
                    <MessageCircle size={12} /> Pelanggan
                  </button>
                  <button
                    onClick={() => sendReminderTechnician(o)}
                    data-testid={`reminder-wa-technician-${o.id}`}
                    title="Kirim pengingat ke teknisi"
                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2.5 py-1.5 border border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB]/10"
                  >
                    <Send size={12} /> Teknisi
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-[#E4E4E7]" data-testid="city-customers-chart">
          <div className="px-6 py-4 border-b border-[#E4E4E7] flex items-center gap-2">
            <MapPin size={14} />
            <div className="label-cap">Pelanggan per Kota · Bulan Berjalan</div>
          </div>
          <div className="p-4">
            {insights.cities.length === 0 ? (
              <div className="p-6 text-sm text-[#52525B]">Belum ada order terjadwal bulan ini.</div>
            ) : (
              <>
                <div style={{ width: "100%", height: Math.max(200, insights.cities.length * 30) }}>
                  <ResponsiveContainer>
                    <BarChart data={insights.cities} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 0 }}>
                      <CartesianGrid stroke="#E4E4E7" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: "#52525B" }} axisLine={{ stroke: "#E4E4E7" }} tickLine={false} domain={[0, Math.max(chartMax + 1, 3)]} />
                      <YAxis type="category" dataKey="city" width={110} tick={{ fontSize: 10, fill: "#52525B" }} axisLine={false} tickLine={false} interval={0} />
                      <Tooltip
                        cursor={{ fill: "#F4F4F5" }}
                        contentStyle={{ borderRadius: 0, border: "1px solid #E4E4E7", fontSize: 12 }}
                        formatter={(value) => [`${value} pelanggan`, "Jumlah"]}
                      />
                      <Bar dataKey="customers" fill="#002FA7" barSize={16} cursor="pointer" onClick={(d) => d && d.city && openCityOrders(d.city)} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="px-2 pt-3 border-t border-[#E4E4E7] mt-3 flex items-center justify-between">
                  <span className="label-cap">Total Pelanggan Bulan Ini</span>
                  <span className="font-mono text-sm font-bold" data-testid="city-customers-total">{insights.cities.reduce((s, c) => s + (c.customers || 0), 0)}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Upcoming + Status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="lg:col-span-2 bg-white border border-[#E4E4E7]">
          <div className="px-4 sm:px-6 py-4 border-b border-[#E4E4E7] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar size={14} />
              <div className="label-cap text-[10px] sm:text-xs">Jadwal Mendatang</div>
            </div>
            <Link to="/orders" className="text-xs font-bold uppercase tracking-wider text-[#002FA7]" data-testid="view-all-orders-link">Lihat Semua →</Link>
          </div>
          <div>
            {data.upcoming.length === 0 && (
              <div className="p-6 text-sm text-[#52525B]">Belum ada jadwal pekerjaan.</div>
            )}
            {data.upcoming.map((o) => (
              <div key={o.id} className="px-4 sm:px-6 py-3 sm:py-4 border-b border-[#E4E4E7] last:border-b-0 row-hover flex items-start sm:items-center gap-2 sm:gap-4 flex-wrap sm:flex-nowrap" data-testid={`upcoming-order-${o.id}`}>
                <div className="font-mono text-[10px] sm:text-xs w-full sm:w-32 sm:shrink-0"><Link to={`/orders?open=${o.id}`} data-testid={`upcoming-orderno-${o.order_no}`} className="text-[#002FA7] hover:underline font-bold">{o.order_no}</Link> · <span className="sm:hidden text-[#52525B]">{o.schedule_date}</span></div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{o.customer?.name || "-"}</div>
                  <div className="text-xs text-[#52525B] truncate">{(o.items || []).map(i => i.description).join(", ")}</div>
                </div>
                <div className="hidden sm:block text-xs font-mono text-[#52525B] w-24">{o.schedule_date}</div>
                <span className={`text-[10px] sm:text-xs font-bold px-2 py-1 border whitespace-nowrap ${STATUS_COLORS[o.work_status] || ""}`}>{o.work_status}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-[#E4E4E7]">
          <div className="px-4 sm:px-6 py-4 border-b border-[#E4E4E7]">
            <div className="label-cap text-[10px] sm:text-xs">Status Pekerjaan (rentang)</div>
          </div>
          <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
            {Object.keys(STATUS_COLORS).map((s) => (
              <div key={s} className="flex items-center justify-between">
                <span className={`text-[10px] sm:text-xs font-bold px-2 py-1 border ${STATUS_COLORS[s]}`}>{s}</span>
                <span className="font-mono text-xl sm:text-2xl font-black">{data.status_counts?.[s] || 0}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-[#E4E4E7] px-4 sm:px-6 py-4">
            <div className="label-cap mb-3 text-[10px] sm:text-xs">Status Pembayaran</div>
            {(data.payment_summary || []).map((p) => (
              <div key={p._id} className="flex items-center justify-between py-1.5 gap-2">
                <span className="text-xs sm:text-sm">{p._id}</span>
                <span onClick={toggleMoney} className="font-mono text-xs sm:text-sm text-right cursor-pointer select-none" title="Klik untuk lihat/sembunyikan">{p.count} · {money(p.total)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-[#E4E4E7] px-4 sm:px-6 py-4" data-testid="payment-detail-month">
            <div className="label-cap mb-3 text-[10px] sm:text-xs">Rincian Pembayaran · Bulan Berjalan</div>
            {(insights.payments || []).length === 0 && (
              <div className="text-xs text-[#52525B]">Belum ada order bulan ini.</div>
            )}
            {(insights.payments || []).map((p) => (
              <div key={p.label} className="flex items-center justify-between py-1.5 gap-2 flex-wrap">
                <span className={`text-xs sm:text-sm ${p.label === "Belum Dibayar" ? "text-[#DC2626] font-bold" : ""}`}>{p.label}</span>
                <span onClick={toggleMoney} className="font-mono text-xs sm:text-sm text-right cursor-pointer select-none" title="Klik untuk lihat/sembunyikan">{p.count} · {money(p.total)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#DC2626]/40 mt-6" data-testid="unpaid-reminder-card">
        <div className="px-4 sm:px-6 py-4 border-b border-[#E4E4E7] flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Wallet size={14} className="text-[#DC2626]" />
            <span className="label-cap text-[10px] sm:text-xs">Selesai — Belum Lunas ({unpaid.count})</span>
          </div>
          <span onClick={toggleMoney} className="text-xs font-mono font-bold text-[#DC2626] cursor-pointer select-none" title="Klik untuk lihat/sembunyikan">Sisa tagihan: {money(unpaid.total_sisa || 0)}</span>
        </div>
        <div className="max-h-72 overflow-y-auto">
          {(unpaid.orders || []).length === 0 && (
            <div className="p-6 text-sm text-[#52525B]">Semua order selesai sudah lunas. 🎉</div>
          )}
          {(unpaid.orders || []).map((o) => (
            <div key={o.id} className="px-4 sm:px-6 py-3 border-b border-[#E4E4E7] last:border-b-0 row-hover flex items-center gap-2 sm:gap-4 flex-wrap sm:flex-nowrap" data-testid={`unpaid-order-${o.order_no}`}>
              <div className="font-mono text-[10px] sm:text-xs w-full sm:w-40 sm:shrink-0"><Link to={`/orders?open=${o.id}`} data-testid={`unpaid-orderno-${o.order_no}`} className="text-[#002FA7] hover:underline font-bold">{o.order_no}</Link> <span className="text-[#52525B]">· {o.schedule_date}</span></div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate">{o.customer_name || "-"}</div>
                <div className="text-xs text-[#52525B]">{o.payment_status}{o.dp_amount > 0 ? ` · DP ${money(o.dp_amount)}` : ""}</div>
              </div>
              <div onClick={toggleMoney} className="font-mono text-xs sm:text-sm font-bold text-[#DC2626] whitespace-nowrap cursor-pointer select-none" title="Klik untuk lihat/sembunyikan">{money(o.sisa)}</div>
              {o.customer_phone && (
                <button
                  onClick={() => sendWaSmart({ enabled: waGateway, phone: o.customer_phone, name: o.customer_name || "", text: `Halo KK ${o.customer_name}, pekerjaan order *${o.order_no}* telah selesai. Mohon penyelesaian sisa pembayaran sebesar *${formatIDR(o.sisa)}*. Terima kasih.` })}
                  data-testid={`unpaid-wa-${o.order_no}`}
                  className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider border border-[#059669] text-[#059669] hover:bg-[#059669] hover:text-white flex items-center gap-1 whitespace-nowrap"
                >
                  <MessageCircle size={12} /> Tagih
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {bulkOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => !bulkSending && setBulkOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#E4E4E7] max-w-sm w-full p-6" data-testid="bulk-send-modal">
            <h3 className="font-heading text-lg font-bold mb-1">Kirim Pengingat ke Semua</h3>
            <p className="text-xs text-[#52525B] mb-4">{(reminders.orders || []).length} order terjadwal {daysAhead === 0 ? "HARI INI" : "BESOK"} ({reminders.target_date}). Pesan dikirim otomatis via WA Gateway (template).</p>
            <div className="space-y-2 mb-6">
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox" checked={bulkCust} onChange={(e) => setBulkCust(e.target.checked)} data-testid="bulk-send-customers" className="w-4 h-4 accent-[#002FA7]" />
                <span>Pelanggan (template <span className="font-mono text-xs">pengingat</span>)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox" checked={bulkTech} onChange={(e) => setBulkTech(e.target.checked)} data-testid="bulk-send-technicians" className="w-4 h-4 accent-[#002FA7]" />
                <span>Teknisi (template <span className="font-mono text-xs">jadwal_teknisi</span>)</span>
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setBulkOpen(false)} disabled={bulkSending} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Batal</button>
              <button onClick={sendBulk} disabled={bulkSending} data-testid="bulk-send-confirm" className="px-4 py-2 text-xs font-bold uppercase bg-[#002FA7] text-white disabled:opacity-60 flex items-center gap-1">
                <Send size={12} /> {bulkSending ? "Mengirim…" : "Kirim Sekarang"}
              </button>
            </div>
          </div>
        </div>
      )}

      {cityModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setCityModal(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#E4E4E7] max-w-2xl w-full max-h-[80vh] flex flex-col" data-testid="city-orders-modal">
            <div className="px-4 sm:px-6 py-4 border-b border-[#E4E4E7] flex items-center justify-between gap-2">
              <div>
                <div className="label-cap text-[10px]">Order Bulan Berjalan · {insights.month}</div>
                <h2 className="font-heading text-lg sm:text-xl font-bold flex items-center gap-2"><MapPin size={16} /> {cityModal} <span className="font-mono text-sm text-[#52525B]">({cityOrders.length} order)</span></h2>
              </div>
              <button onClick={() => setCityModal(null)} className="p-2 hover:bg-[#F4F4F5] text-xl leading-none" data-testid="city-orders-close">✕</button>
            </div>
            <div className="overflow-y-auto flex-1">
              {cityLoading && <div className="p-6 text-sm text-[#52525B]">Memuat…</div>}
              {!cityLoading && cityOrders.length === 0 && <div className="p-6 text-sm text-[#52525B]">Tidak ada order di kota ini bulan ini.</div>}
              {!cityLoading && cityOrders.map((o) => (
                <div key={o.id} className="px-4 sm:px-6 py-3 border-b border-[#E4E4E7] last:border-b-0 row-hover flex items-center gap-2 sm:gap-4 flex-wrap sm:flex-nowrap" data-testid={`city-order-${o.order_no}`}>
                  <div className="font-mono text-[10px] sm:text-xs w-full sm:w-36 sm:shrink-0"><Link to={`/orders?open=${o.id}`} data-testid={`city-orderno-${o.order_no}`} className="text-[#002FA7] hover:underline font-bold">{o.order_no}</Link> <span className="text-[#52525B]">· {o.schedule_date}</span></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">{o.customer?.name || "-"}</div>
                    <div className="text-xs text-[#52525B] truncate">{(o.items || []).map((i) => i.description).join(", ")}</div>
                  </div>
                  <span className={`text-[10px] sm:text-xs font-bold px-2 py-1 border whitespace-nowrap ${STATUS_COLORS[o.work_status] || ""}`}>{o.work_status}</span>
                  <span onClick={toggleMoney} className="font-mono text-xs font-bold whitespace-nowrap cursor-pointer select-none" title="Klik untuk lihat/sembunyikan">{money(o.total)}</span>
                </div>
              ))}
            </div>
            <div className="px-4 sm:px-6 py-3 border-t border-[#E4E4E7] flex justify-end">
              <Link to="/orders" className="text-xs font-bold uppercase tracking-wider text-[#002FA7]" data-testid="city-orders-goto">Buka Halaman Order →</Link>
            </div>
          </div>
        </div>
      )}
      {placeTech && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => !placeSaving && setPlaceTech(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#E4E4E7] max-w-2xl w-full max-h-[92vh] overflow-y-auto" data-testid="placement-modal">
            <div className="px-4 sm:px-6 py-4 border-b border-[#E4E4E7] flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="label-cap text-[10px]">Teknisi · NIK {placeTech.nik}</div>
                <h2 className="font-heading text-lg font-bold truncate">{placeTech.name}</h2>
                <div className="text-xs text-[#52525B]">Base asal: <b>{baseLabel(placeTech.home_base) || placeTech.base_location || "-"}</b></div>
              </div>
              <button onClick={() => setPlaceTech(null)} className="p-2 hover:bg-[#F4F4F5] text-xl leading-none" data-testid="placement-close">✕</button>
            </div>
            <div className="px-4 sm:px-6 pt-4 pb-4 border-b border-[#E4E4E7]">
              <TechPresence technicianId={placeTech.technician_id} />
            </div>
            <div className="p-4 sm:p-6 space-y-3">
              <div className="label-cap text-[10px]">Penempatan Base</div>
              <label className={`flex items-start gap-2 border px-3 py-2 cursor-pointer ${placeForm.outside_base ? "border-[#7C3AED] bg-[#7C3AED]/5" : "border-[#E4E4E7] hover:bg-[#F4F4F5]"}`}>
                <input type="checkbox" data-testid="placement-outside-base" checked={placeForm.outside_base}
                  onChange={(e) => setPlaceForm({ ...placeForm, outside_base: e.target.checked, outside_base_start: e.target.checked && !placeForm.outside_base_start ? (attendance.date || "") : placeForm.outside_base_start })}
                  className="mt-0.5 accent-[#7C3AED]" />
                <span className="text-sm">
                  <span className="font-bold">Penempatan luar base lokasi</span>
                  <span className="block text-[11px] text-[#52525B]">Payroll: luar kota otomatis YA selama periode (kecuali libur).</span>
                </span>
              </label>
              {placeForm.outside_base && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="label-cap text-[10px] mb-1">Mulai</div>
                      <input type="date" data-testid="placement-start" value={placeForm.outside_base_start} onChange={(e) => setPlaceForm({ ...placeForm, outside_base_start: e.target.value })} className="w-full px-2 py-1.5 border border-[#E4E4E7] text-sm outline-none focus:border-[#7C3AED] font-mono" />
                    </div>
                    <div>
                      <div className="label-cap text-[10px] mb-1">Sampai *</div>
                      <input type="date" data-testid="placement-end" value={placeForm.outside_base_end} min={placeForm.outside_base_start || undefined} onChange={(e) => setPlaceForm({ ...placeForm, outside_base_end: e.target.value })} className="w-full px-2 py-1.5 border border-[#7C3AED]/50 text-sm outline-none focus:border-[#7C3AED] font-mono" />
                    </div>
                  </div>
                  <div>
                    <div className="label-cap text-[10px] mb-1">Ditempatkan di base lain (opsional)</div>
                    <select data-testid="placement-to-base" value={placeForm.outside_base_to} onChange={(e) => setPlaceForm({ ...placeForm, outside_base_to: e.target.value })} className="w-full px-2 py-1.5 border border-[#E4E4E7] text-sm outline-none focus:border-[#7C3AED] bg-white">
                      <option value="">— Tidak ditempatkan di base lain —</option>
                      {(attendance.bases || []).filter((b) => b.key !== placeTech.home_base).map((b) => (
                        <option key={b.key} value={b.key}>Base {b.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <div className="label-cap text-[10px] mb-1">Catatan (opsional)</div>
                    <input type="text" data-testid="placement-note" value={placeForm.note} onChange={(e) => setPlaceForm({ ...placeForm, note: e.target.value })} placeholder="mis. bantu proyek client X" className="w-full px-2 py-1.5 border border-[#E4E4E7] text-sm outline-none focus:border-[#7C3AED]" />
                  </div>
                  <div className="text-[11px] text-[#52525B] border border-dashed border-[#E4E4E7] px-2 py-1.5">
                    Remark: <b className="text-[#7C3AED]">{placeForm.outside_base_to ? `Ditempatkan di base ${baseLabel(placeForm.outside_base_to)}` : "Penempatan luar base lokasi"}{placeForm.note.trim() ? ` — ${placeForm.note.trim()}` : ""}</b>
                  </div>
                </>
              )}
              {!placeForm.outside_base && placeTech.outside_base && (
                <div className="text-[11px] text-[#D97706]">Penempatan saat ini ({placeTech.outside_base_remark || "luar base"}) akan dihapus.</div>
              )}
            </div>
            <div className="px-4 sm:px-6 py-3 border-t border-[#E4E4E7] flex justify-end gap-2">
              <button onClick={() => setPlaceTech(null)} disabled={placeSaving} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Batal</button>
              <button onClick={savePlacement} disabled={placeSaving} data-testid="placement-save" className="px-4 py-2 text-xs font-bold uppercase bg-[#7C3AED] text-white disabled:opacity-60">{placeSaving ? "Menyimpan…" : "Simpan"}</button>
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
};

export default Dashboard;
